import { cn } from '@/utilities/ui'
import { File, FileCheck, FileX, Signature } from 'lucide-react'
import * as React from 'react'

/*
 * NOTE — `color/status/error-300` (#fca5a5) still has no token in globals.css, so the
 * `focus-error` effect below is written as an arbitrary value. Once
 * `--color-error-300: #fca5a5` lands in the `@theme` block this should become
 * `ring-error-300`. Tracked on the parity escalation list.
 */

/**
 * Wilson Property canonical Textarea component.
 *
 * Figma spec (component set 2198:41658 — verified 2026-07-22):
 *   Model     FILLED grey field. Fill `theme/muted` #e0dfe0, radius `sm` 2px, NO border on
 *             Empty / Placeholder / Value / Focused / Disabled.
 *   Size      64px tall in every state, padding px-3 (`padding/p-3`) py-2 (`padding/p-2`).
 *             12px + 3 × 16px lines + 8px + 8px = 64px, so the default `rows={3}` the
 *             Payload field passes lands exactly on the Figma height. `min-h-16` is the floor.
 *   Text      Body/bodySm — Lato light 300, `font/size/sm` 14 / `font/lineHeight/sm` 16
 *             (the `text-body-sm` token), colour `theme/card-foreground` (#000);
 *             placeholder `theme/muted-foreground` #5a595d.
 *   Shadow    `shadows/shadow-xs` 0 1px 2px rgba(0,0,0,0.05).
 *   Focus     `focus` effect only — 0 0 0 3px `theme/ring` #8cc64080, no border change and
 *             NO shadow/xs (2198:41670), hence `focus-visible:shadow-none`.
 *   Error     1px solid `theme/destructive` #ef4444, no ring (2198:41666). The value text
 *             stays black — unlike Input, the Textarea error text node 2198:41667 is
 *             `theme/card-foreground`. Error Focused (2198:41668) adds `focus-error`
 *             0 0 0 3px `color/status/error-300` #fca5a5 at full opacity.
 *             Drawn with `inset-ring` rather than `border`: the intrinsic rows-driven
 *             height is `auto`, so a real border pushed the field to 66px while Figma is
 *             64px in all seven states. An inset ring paints inside the box and costs
 *             no layout.
 *   Disabled  opacity 50% (2198:41664).
 *   Resize    no state in the set shows a resize grabber — `resize-none`.
 */
const Textarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = ({
  className,
  ...props
}) => {
  return (
    <textarea
      data-slot="textarea"
      className={cn(
        'flex min-h-16 w-full resize-none rounded-sm bg-muted px-3 py-2 text-body-sm font-light text-foreground shadow-xs placeholder:text-muted-foreground placeholder:font-light transition-[color,box-shadow] focus-visible:shadow-none focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:inset-ring aria-invalid:inset-ring-destructive aria-invalid:focus-visible:ring-[#fca5a5]!',
        className,
      )}
      {...props}
    />
  )
}

type TextareaUploadState = 'empty' | 'placeholder' | 'value' | 'error' | 'disabled'

const UPLOAD_STATE_CONTENT: Record<
  Exclude<TextareaUploadState, 'empty'>,
  { Icon: typeof File; defaultLabel: string; tone: string }
> = {
  // 2206:43366 — `file`, Body/bodySm in theme/muted-foreground.
  placeholder: {
    Icon: File,
    defaultLabel: 'Select or drag and drop files.',
    tone: 'text-muted-foreground',
  },
  // 2206:43368 — `file-check`, theme/card-foreground.
  value: { Icon: FileCheck, defaultLabel: 'Files successfully attached.', tone: 'text-foreground' },
  // 2206:43372 — `file-x`, theme/destructive, dashed destructive border.
  error: { Icon: FileX, defaultLabel: 'Select or drag and drop files.', tone: 'text-destructive' },
  // 2206:43370 — `file-x`, theme/card-foreground, whole field at 50%.
  disabled: { Icon: FileX, defaultLabel: 'Attach a file unavailable.', tone: 'text-foreground' },
}

/**
 * Wilson Property canonical file drop-zone — Figma "Textarea+upload" 2206:43364.
 *
 * Same 598×64 filled shell as {@link Textarea} (bg `theme/muted`, radius `sm`, px-3 py-2,
 * shadow/xs) but centred flex-col with `gap/gap-1,5` 6px: a 24px lucide file glyph above a
 * Body/bodySm line. Seven states in Figma; `focused` and `error focused` are the CSS focus
 * of the two content states, so this takes five content values plus real focus-within.
 *
 * The error outline is `outline-dashed` rather than a border — Figma draws it dashed
 * (2206:43372 / 2206:43374) and an outline costs no layout, keeping the box at 64px.
 *
 * Presentational only: it renders a visually-hidden `<input type="file">` inside a label so
 * the whole zone is clickable. Wire `state` from whatever form layer consumes it — the Figma
 * field wrapper (2206:43378) that adds the label and helper text belongs to that layer.
 */
const TextareaUpload: React.FC<
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & {
    state?: TextareaUploadState
    label?: string
    className?: string
  }
> = ({ state = 'placeholder', label, className, disabled, ...props }) => {
  const isDisabled = disabled ?? state === 'disabled'
  const isError = state === 'error'
  const content = state === 'empty' ? null : UPLOAD_STATE_CONTENT[state]

  return (
    <label
      data-slot="textarea-upload"
      data-state={state}
      aria-invalid={isError || undefined}
      className={cn(
        'relative flex h-16 w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-sm bg-muted px-3 py-2 text-body-sm font-light shadow-xs transition-[color,box-shadow] focus-within:shadow-none focus-within:ring-[3px] focus-within:ring-ring focus-within:ring-offset-0 aria-invalid:outline-1 aria-invalid:outline-dashed aria-invalid:-outline-offset-1 aria-invalid:outline-destructive aria-invalid:focus-within:ring-[#fca5a5]!',
        isDisabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      <input type="file" className="sr-only" disabled={isDisabled} {...props} />
      {content && (
        <>
          <content.Icon className={cn('size-6 shrink-0', content.tone)} aria-hidden="true" />
          <span className={cn('whitespace-nowrap', content.tone)}>
            {label ?? content.defaultLabel}
          </span>
        </>
      )}
    </label>
  )
}

type SignatureTextareaState = 'empty' | 'placeholder' | 'value' | 'error' | 'disabled'

/**
 * Wilson Property canonical signature pad — Figma "Signature textarea" 2357:90056.
 *
 * 598×128 (double the standard 64px textarea): the same filled `theme/muted` shell, radius
 * `sm`, px-3 py-2, shadow/xs, with two stacked regions separated by `gap/gap-1,5` 6px —
 *   1. a flex-1 centred column holding the 24px lucide `signature` glyph over a Body/bodySm
 *      prompt ("Sign here"; "Signature Unavailable" when disabled, 2357:90066), and
 *   2. a full-width 1px signature rule (theme/destructive in the error states, 2357:90267)
 *      with an uppercase Body/bodyXs 12/14 "Clear" affordance right-aligned 8px below it.
 *
 * Presentational only — `children` slots in whatever capture surface (canvas, etc.) a caller
 * supplies; the field wrapper (2357:90076) with its label belongs to the form layer.
 */
const SignatureTextarea: React.FC<{
  state?: SignatureTextareaState
  label?: string
  clearLabel?: string
  onClear?: () => void
  className?: string
  children?: React.ReactNode
}> = ({ state = 'placeholder', label, clearLabel = 'Clear', onClear, className, children }) => {
  const isDisabled = state === 'disabled'
  const isError = state === 'error'
  const prompt = label ?? (isDisabled ? 'Signature Unavailable' : 'Sign here')
  const promptTone = isError
    ? 'text-destructive'
    : state === 'value' || isDisabled
      ? 'text-foreground'
      : 'text-muted-foreground'

  return (
    <div
      data-slot="signature-textarea"
      data-state={state}
      aria-invalid={isError || undefined}
      className={cn(
        'relative flex h-32 w-full flex-col items-center justify-center gap-1.5 rounded-sm bg-muted px-3 py-2 text-body-sm font-light shadow-xs transition-[color,box-shadow] focus-within:shadow-none focus-within:ring-[3px] focus-within:ring-ring focus-within:ring-offset-0 aria-invalid:outline-1 aria-invalid:outline-dashed aria-invalid:-outline-offset-1 aria-invalid:outline-destructive aria-invalid:focus-within:ring-[#fca5a5]!',
        isDisabled && 'opacity-50',
        className,
      )}
    >
      {state !== 'empty' && (
        <>
          <div className="relative flex min-h-px flex-1 flex-col items-center justify-center gap-1.5">
            {children ?? (
              <>
                <Signature className={cn('size-6 shrink-0', promptTone)} aria-hidden="true" />
                <span className={cn('whitespace-nowrap', promptTone)}>{prompt}</span>
              </>
            )}
          </div>
          {!isDisabled && (
            <div className="flex w-full shrink-0 flex-col items-end gap-2">
              <div className={cn('h-px w-full', isError ? 'bg-destructive' : 'bg-foreground')} />
              <button
                type="button"
                onClick={onClear}
                className="text-body-xs font-light uppercase text-muted-foreground"
              >
                {clearLabel}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export { Textarea, TextareaUpload, SignatureTextarea }
export type { TextareaUploadState, SignatureTextareaState }
